import React, { useCallback, useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChartBar, ListBullets, GridFour, FileXls, FilePdf } from "@phosphor-icons/react";
import { ReportSummaryChip } from "@/components/spb/SpbReportComponents";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

function monthLabel(bulan) {
  // "2026-06" -> "Jun 2026"
  const [y, m] = (bulan || "").split("-");
  const idx = parseInt(m, 10) - 1;
  if (!y || isNaN(idx) || idx < 0 || idx > 11) return bulan || "?";
  return `${MONTH_NAMES[idx]} ${y}`;
}

const num = (v) => Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 3 });

function PctBadge({ dibuat, termuat, testid }) {
  if (!dibuat) return <span className="text-muted-foreground" data-testid={testid}>–</span>;
  const pct = (Number(termuat || 0) / Number(dibuat)) * 100;
  const tone = pct >= 80 ? "text-emerald-700" : pct >= 40 ? "text-amber-600" : "text-red-600";
  return <span className={`font-mono font-semibold ${tone}`} data-testid={testid}>{pct.toLocaleString("id-ID", { maximumFractionDigits: 1 })}%</span>;
}

export default function DistrikMonthlyReport() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [distrikId, setDistrikId] = useState("");
  const [districts, setDistricts] = useState([]);
  const [data, setData] = useState({ rows: [], months: [], matrix: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("list"); // list | matrix
  const loadToken = React.useRef(0);

  const load = useCallback(async () => {
    const my = ++loadToken.current;
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (distrikId) params.distrik_id = distrikId;
      const { data: d } = await api.get("/reports/distrik-monthly", { params });
      if (my !== loadToken.current) return;
      setData(d);
    } catch {
      if (my === loadToken.current) setData({ rows: [], months: [], matrix: [], summary: {} });
    } finally {
      if (my === loadToken.current) setLoading(false);
    }
  }, [dateFrom, dateTo, distrikId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/districts").then(({ data: d }) => setDistricts(Array.isArray(d) ? d : (d?.items || []))).catch(() => setDistricts([]));
  }, []);

  const buildQS = () => {
    const p = new URLSearchParams();
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (distrikId) p.set("distrik_id", distrikId);
    return p.toString();
  };

  const downloadExport = async (format) => {
    const url = `${API_BASE}/reports/distrik-monthly/export.${format}?${buildQS()}`;
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `laporan_distrik_bulanan.${format === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Export ${format.toUpperCase()} berhasil`);
    } catch (e) { toast.error(`Export gagal: ${e.message}`); }
  };

  const s = data.summary || {};

  return (
    <div className="max-w-[1400px] mx-auto p-4 lg:p-8 space-y-6" data-testid="distrik-monthly-page">
      <div className="border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <ChartBar size={28} weight="duotone" className="text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Laporan Semen per Bulan per Distrik</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Total semen yang dibuat dari SPB (termasuk dibatalkan) dibanding yang sudah termuat, dirinci per satuan.
            </p>
          </div>
        </div>
      </div>

      {/* Filter + export bar */}
      <div className="border border-border bg-card p-4 flex flex-wrap items-end gap-4" data-testid="filter-bar">
        <div>
          <label className="overline block mb-1 text-muted-foreground">Dari Tanggal</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="border border-border bg-background px-3 py-2 text-sm rounded-md" data-testid="filter-date-from" />
        </div>
        <div>
          <label className="overline block mb-1 text-muted-foreground">Sampai Tanggal</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="border border-border bg-background px-3 py-2 text-sm rounded-md" data-testid="filter-date-to" />
        </div>
        <div>
          <label className="overline block mb-1 text-muted-foreground">Distrik</label>
          <Select value={distrikId || "__all__"} onValueChange={(v) => setDistrikId(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-56 h-10 rounded-md" data-testid="filter-distrik">
              <SelectValue placeholder="Semua Distrik" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__" data-testid="filter-distrik-all">Semua Distrik</SelectItem>
              {districts.map((d) => (
                <SelectItem key={d.id} value={d.id} data-testid={`filter-distrik-${d.id}`}>
                  {d.kode ? `${d.kode} - ${d.nama}` : d.nama}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} className="rounded-md" data-testid="apply-filter-btn">Terapkan</Button>
        <div className="flex-1" />
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadExport("xlsx")} className="rounded-md gap-2" data-testid="export-xlsx-btn">
            <FileXls size={18} weight="duotone" /> Excel
          </Button>
          <Button variant="outline" onClick={() => downloadExport("pdf")} className="rounded-md gap-2" data-testid="export-pdf-btn">
            <FilePdf size={18} weight="duotone" /> PDF
          </Button>
        </div>
      </div>

      {s.date_from && (
        <div className="text-xs text-muted-foreground" data-testid="active-range">
          Periode aktif: <strong>{s.date_from}</strong> s/d <strong>{s.date_to}</strong>
        </div>
      )}

      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-chips">
        <ReportSummaryChip label="Total Dibuat" value={num(s.total_dibuat)} />
        <ReportSummaryChip label="Total Termuat" value={num(s.total_termuat)} />
        <ReportSummaryChip label="Baris Data" value={s.row_count ?? 0} />
        <ReportSummaryChip label="Jumlah Bulan" value={data.months.length} />
      </div>

      {/* View toggle */}
      <div className="flex gap-2">
        <Button variant={view === "list" ? "default" : "outline"} onClick={() => setView("list")}
          className="rounded-md gap-2" data-testid="view-list-btn">
          <ListBullets size={18} /> Daftar
        </Button>
        <Button variant={view === "matrix" ? "default" : "outline"} onClick={() => setView("matrix")}
          className="rounded-md gap-2" data-testid="view-matrix-btn">
          <GridFour size={18} /> Matriks
        </Button>
      </div>

      {loading && <div className="text-sm text-muted-foreground" data-testid="loading">Memuat…</div>}

      {/* LIST VIEW */}
      {!loading && view === "list" && (
        <div className="border border-border overflow-x-auto" data-testid="list-table">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold">Bulan</th>
                <th className="px-3 py-2 font-semibold">Distrik</th>
                <th className="px-3 py-2 font-semibold">Satuan</th>
                <th className="px-3 py-2 font-semibold text-right">Dibuat</th>
                <th className="px-3 py-2 font-semibold text-right">Termuat</th>
                <th className="px-3 py-2 font-semibold text-right">% Termuat</th>
                <th className="px-3 py-2 font-semibold text-right">Jumlah SPB</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Tidak ada data pada periode ini.</td></tr>
              )}
              {data.rows.map((r, i) => (
                <tr key={i} className="border-t border-border hover:bg-muted/40" data-testid={`list-row-${i}`}>
                  <td className="px-3 py-2">{monthLabel(r.bulan)}</td>
                  <td className="px-3 py-2 font-medium">{r.distrik_nama}</td>
                  <td className="px-3 py-2">{r.satuan}</td>
                  <td className="px-3 py-2 text-right font-mono">{num(r.total_dibuat)}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700">{num(r.total_termuat)}</td>
                  <td className="px-3 py-2 text-right"><PctBadge dibuat={r.total_dibuat} termuat={r.total_termuat} testid={`list-pct-${i}`} /></td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* MATRIX VIEW */}
      {!loading && view === "matrix" && (
        <div className="border border-border overflow-x-auto" data-testid="matrix-table">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold sticky left-0 bg-muted z-10">Distrik</th>
                <th className="px-3 py-2 font-semibold">Satuan</th>
                {data.months.map((m) => (
                  <th key={m} className="px-3 py-2 font-semibold text-right whitespace-nowrap">{monthLabel(m)}</th>
                ))}
                <th className="px-3 py-2 font-semibold text-right">Total</th>
                <th className="px-3 py-2 font-semibold text-right">% Termuat</th>
              </tr>
              <tr className="text-[10px] text-muted-foreground">
                <th className="px-3 pb-1 sticky left-0 bg-muted z-10" />
                <th className="px-3 pb-1" />
                {data.months.map((m) => (
                  <th key={m} className="px-3 pb-1 text-right font-normal">Dibuat / Termuat</th>
                ))}
                <th className="px-3 pb-1 text-right font-normal">Dibuat / Termuat</th>
                <th className="px-3 pb-1" />
              </tr>
            </thead>
            <tbody>
              {data.matrix.length === 0 && (
                <tr><td colSpan={data.months.length + 4} className="px-3 py-6 text-center text-muted-foreground">Tidak ada data pada periode ini.</td></tr>
              )}
              {data.matrix.map((row, i) => (
                <tr key={i} className="border-t border-border hover:bg-muted/40" data-testid={`matrix-row-${i}`}>
                  <td className="px-3 py-2 font-medium sticky left-0 bg-card z-10">{row.distrik_nama}</td>
                  <td className="px-3 py-2">{row.satuan}</td>
                  {data.months.map((m) => {
                    const cell = row.cells[m];
                    return (
                      <td key={m} className="px-3 py-2 text-right font-mono whitespace-nowrap">
                        {cell ? (
                          <span>{num(cell.dibuat)} <span className="text-emerald-700">/ {num(cell.termuat)}</span></span>
                        ) : <span className="text-muted-foreground">–</span>}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right font-mono font-semibold whitespace-nowrap">
                    {num(row.total_dibuat)} <span className="text-emerald-700">/ {num(row.total_termuat)}</span>
                  </td>
                  <td className="px-3 py-2 text-right"><PctBadge dibuat={row.total_dibuat} termuat={row.total_termuat} testid={`matrix-pct-${i}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
